import { silentSignOut } from 'reducers/userReducer';

const sessionExpiredHandler = (dispatch) => {
	if (dispatch) {
		dispatch(silentSignOut());
	}
};

/**
 * Get active organization ID from session/localStorage
 */
const getActiveOrganizationId = () => {
	// Try session first (set by middleware)
	const sessionOrgId = sessionStorage.getItem('active_organization_id');
	if (sessionOrgId) return sessionOrgId;

	// Fallback to localStorage (persisted)
	const localOrgId = localStorage.getItem('active_organization_id');
	if (localOrgId) return localOrgId;

	return null;
};

export const get = ({ url, onSuccess, onError, authToken, dispatch }) => {
	let fetchParams = {
		headers: {
			Accept: 'application/json, text/plain, */*',
		},
	};

	if (authToken) {
		const headerValue = authToken.startsWith('Bearer ')
			? authToken
			: `Bearer ${authToken}`;
		fetchParams.headers.authorization = headerValue;
	}

	const activeOrgId = getActiveOrganizationId();
	if (activeOrgId) {
		fetchParams.headers['X-Active-Organization'] = activeOrgId;
	}

	return fetch(new Request(url, fetchParams))
		.then((result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}
			if (!result.ok) {
				return result.json().then((err) => {
					throw err;
				});
			}
			return result.json();
		})
		.then(
			(result) => {
				if (onSuccess) onSuccess(result);
				return result;
			},
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
				throw error;
			},
		);
};

export const post = ({
	url,
	data,
	onSuccess,
	onError,
	authToken,
	dispatch,
}) => {
	let fetchParams = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			Accept: 'application/json, text/plain, */*',
		},
	};

	if (data) {
		fetchParams.body = JSON.stringify(data);
	}

	if (authToken) {
		fetchParams.headers.authorization = `Bearer ${authToken}`;
	}

	const activeOrgId = getActiveOrganizationId();
	if (activeOrgId) {
		fetchParams.headers['X-Active-Organization'] = activeOrgId;
	}

	return fetch(url, fetchParams)
		.then(async (result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}

			if (result.status === 400) {
				return result.json().then((errorData) => {
					throw new Error(errorData?.message || errorData || 'Bad Request');
				});
			}

			if (!result.ok) {
				let errorData = null;
				try {
					errorData = await result.json();
				} catch (parseError) {
					throw new Error(`HTTP ${result.status}`);
				}

				const error = new Error(
					errorData.message || errorData.error || `HTTP ${result.status}`,
				);
				error.status = result.status;
				error.errors = errorData.errors || null;
				// A machine-readable cause, where the API supplies one. Callers
				// branch on this instead of matching on message text.
				error.code = errorData.code || null;
				// `ApiResponse::toArray()` puts the machine-readable failure
				// reason under `error` (e.g. PLAN_LIMIT_EXCEEDED from
				// CheckPlanLimitsResponse), not `code` -- surface it under its
				// own name so callers can branch on the real wire shape without
				// colliding with the `code` field above.
				error.errorCode = errorData.error || null;
				// CheckPlanLimitsResponse::toArray() attaches these structured
				// objects alongside `error`/`message` on a 403. Preserve them on
				// the rejected Error instead of discarding them, so a caller that
				// needs the usage/limit/plan details doesn't have to re-derive
				// them from the message string.
				error.errorDetails = errorData.errorDetails || null;
				error.suggestions = errorData.suggestions || null;
				throw error;
			}

			return result.json();
		})
		.then(
			(result) => {
				if (onSuccess) onSuccess(result);
				return result;
			},
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
				throw error;
			},
		);
};

export const put = ({ url, data, onSuccess, onError, authToken, dispatch }) => {
	let fetchParams = {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			Accept: 'application/json, text/plain, */*',
		},
	};

	if (data) {
		fetchParams.body = JSON.stringify(data);
	}

	if (authToken) {
		fetchParams.headers.authorization = `Bearer ${authToken}`;
	}

	const activeOrgId = getActiveOrganizationId();
	if (activeOrgId) {
		fetchParams.headers['X-Active-Organization'] = activeOrgId;
	}

	return fetch(url, fetchParams)
		.then(async (result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}

			if (!result.ok) {
				try {
					const errorData = await result.json();
					throw new Error(
						errorData.message || errorData.error || `HTTP ${result.status}`,
					);
				} catch (parseError) {
					throw new Error(`HTTP ${result.status}`);
				}
			}

			return result.json();
		})
		.then(
			(result) => {
				if (onSuccess) onSuccess(result);
				return result;
			},
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
				throw error;
			},
		);
};

export const patch = ({ url, data, onSuccess, onError, authToken, dispatch }) => {
	let fetchParams = {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			Accept: 'application/json, text/plain, */*',
		},
	};

	if (data) {
		fetchParams.body = JSON.stringify(data);
	}

	if (authToken) {
		fetchParams.headers.authorization = `Bearer ${authToken}`;
	}

	const activeOrgId = getActiveOrganizationId();
	if (activeOrgId) {
		fetchParams.headers['X-Active-Organization'] = activeOrgId;
	}

	return fetch(url, fetchParams)
		.then(async (result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}

			if (!result.ok) {
				// The parse-only try/catch below must guard nothing but the parse
				// itself: throwing the informative error INSIDE it (as put() still
				// does) would be caught by its own catch and collapsed to a generic
				// `HTTP <status>`, discarding whatever message the server sent.
				let errorData = null;
				try {
					errorData = await result.json();
				} catch (parseError) {
					throw new Error(`HTTP ${result.status}`);
				}

				const error = new Error(
					errorData.message || errorData.error || `HTTP ${result.status}`,
				);
				error.status = result.status;
				error.errors = errorData.errors || null;
				throw error;
			}

			return result.json();
		})
		.then(
			(result) => {
				if (onSuccess) onSuccess(result);
				return result;
			},
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
				throw error;
			},
		);
};

export const del = ({ url, onSuccess, onError, authToken, dispatch }) => {
	let fetchParams = {
		method: 'DELETE',
	};

	if (authToken) {
		const headerValue = authToken.startsWith('Bearer ')
			? authToken
			: `Bearer ${authToken}`;
		fetchParams.headers = {
			authorization: headerValue,
		};
	}

	const activeOrgId = getActiveOrganizationId();
	if (activeOrgId) {
		fetchParams.headers = {
			...fetchParams.headers,
			'X-Active-Organization': activeOrgId,
		};
	}

	return fetch(new Request(url, fetchParams))
		.then(async (result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}
			// Handle 204 No Content (successful deletion with no body)
			if (result.status === 204) {
				return result;
			}
			if (!result.ok) {
				try {
					const errorData = await result.json();
					throw new Error(
						errorData.message || errorData.error || `HTTP ${result.status}`,
					);
				} catch (parseError) {
					// If JSON parsing fails, use the parse error or status code
					throw new Error(parseError.message || `HTTP ${result.status}`);
				}
			}
			return result;
		})
		.then(
			(result) => {
				if (onSuccess) onSuccess(result);
				return result;
			},
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
				throw error;
			},
		);
};

export const upload = ({
	url,
	data,
	onSuccess,
	onError,
	authToken,
	dispatch,
}) => {
	let fetchParams = {
		method: 'POST',
		headers: {
			Accept: 'application/json, text/plain, */*',
		},
		body: data,
	};

	if (authToken) {
		fetchParams.headers.authorization = `Bearer ${authToken}`;
	}

	return fetch(url, fetchParams)
		.then((result) => {
			if (result.status === 401) {
				sessionExpiredHandler(dispatch);
			}

			if (!result.ok) {
				throw new Error(result.json());
			}

			return result.json();
		})
		.then(
			(result) => onSuccess(result),
			(error) => {
				if (typeof onError === 'function') {
					onError(error);
				}
			},
		);
};

export const signInApiRequest = ({
	url,
	data,
	onSuccess,
	onError,
	dispatch,
}) => {
	return fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json;charset=UTF-8',
			Accept: 'application/json, text/plain, */*',
		},
		body: JSON.stringify(data),
	})
		.then((result) => onSuccess(result))
		.catch((error) => onError(error));
};

export const download = ({
	url,
	filename,
	onSuccess,
	onError,
	authToken,
	dispatch,
}) => {
	let fetchParams = {};
	if (authToken) {
		fetchParams.headers = {
			authorization: `Bearer ${authToken}`,
		};
	}

	return fetch(new Request(url, fetchParams))
		.then((response) => response.blob())
		.then((blob) => {
			let url = window.URL.createObjectURL(blob);
			let a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
			a.click();
			a.remove(); //afterwards we remove the element again
		});
};
